import { useCallback } from 'react'
import debounce from 'lodash.debounce'
import type {
  HookFetcherContext,
  MutationHookContext,
} from '@commerce/utils/types'
import { ValidationError } from '@commerce/utils/errors'
import useUpdateLine, { UseUpdateLine } from '@commerce/actual-cart/use-update-line'

import useCart from './use-cart'
import { handler as removeItemHandler } from './use-remove-line'
import type { UpdateItemHook } from '../types/cart'
import {
  getCartId,
  cartLinesUpdateMutation,
  cartToCart,
} from '../utils'
import { Maybe, Mutation, Scalars } from '../schema'


/** Specifies the input fields to create a line item on a checkout. */
export type CartLineUpdateInput = {
  id?: Maybe<Scalars['ID']>
  /** The identifier of the product variant for the line item. */
  merchandiseId: Scalars['ID']
  /** The quantity of the line item. */
  quantity: Scalars['Int']
  /** The identifier of the selling plan for the line item. */
  sellingPlanId?: Scalars['ID']

  attributes?: { key: string, value: any }[]
}

/** The schema’s entry-point for mutations. This acts as the public, top-level API from which all mutation queries must start. */
export type MutationCartLinesUpdateArgs = {
  lines: Array<CartLineUpdateInput>
  cartId: Scalars['ID']
}



export type UpdateLineActionInput<T = any> = T extends any
  ? Partial<UpdateItemHook['actionInput']>
  : UpdateItemHook['actionInput']

export default useUpdateLine as UseUpdateLine<typeof handler>

export const handler = {
  fetchOptions: {
    query: cartLinesUpdateMutation,
  },
  async fetcher({
    input: { itemId, item },
    options,
    fetch,
  }: HookFetcherContext<UpdateItemHook>) {
    if (Number.isInteger(item.quantity)) {
      // Also allow the update hook to remove an item if the quantity is lower than 1
      if (item.quantity! < 1) {
        return removeItemHandler.fetcher({
          options: removeItemHandler.fetchOptions,
          input: { itemId },
          fetch,
        })
      }
    } else if (item.quantity) {
      throw new ValidationError({
        message: 'The item quantity has to be a valid integer',
      })
    }

    const { cartLinesUpdate } = await fetch<
      Mutation,
      MutationCartLinesUpdateArgs
    >({
      ...options,
      variables: {
        cartId: getCartId(),
        lines: [
          {
            id: itemId,
            merchandiseId: item.merchandise?.id,
            quantity: item.quantity ?? 1,
            sellingPlanId: item.sellingPlanId,
            attributes: item.attributes
          },
        ],
      },
    }) as any

    return cartToCart(cartLinesUpdate)
  },
  useHook: ({ fetch }: MutationHookContext<UpdateItemHook>) => <
    T extends any | undefined = undefined
  >(
    ctx: {
      item?: T
      wait?: number
    } = {}
  ) => {
    const { item } = ctx
    const { mutate } = useCart() as any

    return useCallback(
      debounce(async (input: UpdateLineActionInput<T>) => {
        const itemId = input.id ?? (item as any)?.id
        const merchandiseId = input?.merchandise?.merchandiseId ?? (item as any)?.merchandise?.id
        const sellingPlanId = input.sellingPlanId ?? (item as any)?.sellingPlanId
        const attributes = input.attributes ?? (item as any)?.attributes;

        if (!itemId || !merchandiseId) {
          throw new ValidationError({
            message: 'Invalid input used for this operation',
          })
        }

        const data = await fetch({
          input: {
            item: {
              merchandise: {
                id: merchandiseId
              },
              quantity: input.quantity,
              sellingPlanId,
              attributes
            },
            itemId,
          },
        })
        await mutate(data, false)
        return data
      }, ctx.wait ?? 500),
      [fetch, mutate]
    )
  },
}
